import React from 'react';
import PropTypes from 'prop-types';
import errorReporting from 'APP_ROOT/services/errorReporting';

const makeCancelable = (promise) => {
    let hasCanceled_ = false;

    const wrappedPromise = new Promise((resolve, reject) => {
        promise.then(
            val => hasCanceled_ ? reject({isCanceled: true}) : resolve(val),
            error => hasCanceled_ ? reject({isCanceled: true}) : reject(error)
        );
    });

    return {
        promise: wrappedPromise,
        then: (onsuccess, onerror) => wrappedPromise.then(onsuccess, onerror),
        cancel() {
            hasCanceled_ = true;
        },
    };
};

const MINIMUM_TIMEOUT = 5000;

/**
 * UpdateWatcher periodically fetches a text file (/version.txt) containing the currently
 * deployed version and compares it to the version defined in the executing script.
 * If there is a difference between the running version and the deployed version, a function
 * passed in the props is called to notify its parent that an update was found.
 **/
export default class UpdateWatcher extends React.PureComponent {

    componentDidMount() {
        if (this.props.active) {
            this.activate();
        }
    }

    componentWillUnmount() {
        this.deactivate();
        if (this.checkInProgress) {
            this.checkInProgress.cancel();
        }
    }

    componentDidUpdate(oldProps) {
        if (oldProps.active !== this.props.active) {
            if (this.props.active) {
                this.activate();
            } else {
                this.deactivate();
            }
        }
    }

    activate = () => {
        this.nextCheckTimeout = setTimeout(this.checkForUpdate, this.timeUntilNextCheck(this.props.lastCheckTime));
    }

    deactivate = () => {
        if (this.nextCheckTimeout) {
            clearTimeout(this.nextCheckTimeout);
            this.nextCheckTimeout = 0;
        }
    }

    rescheduleCheckForUpdate = isError => {
        if(isError && !this.errorRetryCount) {
            this.errorRetryCount = 1;
            this.nextCheckTimeout = setTimeout(this.checkForUpdate, MINIMUM_TIMEOUT);
        } else {
            this.errorRetryCount = 0;
            this.nextCheckTimeout = setTimeout(this.checkForUpdate, this.timeUntilNextCheck());
        }
    }

    checkForUpdate = () => {
        this.checkInProgress = makeCancelable(fetch('/version.txt'))
        return this.checkInProgress
            .then(response => {
                if (response.status === 200) {
                    return response.text();
                }
                throw new Error(`response.status = ${response.status}`);
            }, error => {
                errorReporting.error(error);
                // retry only once
                this.rescheduleCheckForUpdate(true);
                throw new Error('ignore');
            }).then(text =>
                text.trim()
            ).then(text => {
                return (text && text !== this.props.version);
            }).then(updateFound => {
                this.checkInProgress = null;
                this.rescheduleCheckForUpdate();
                this.props.onCheckResult(updateFound);
                return updateFound;
            }).catch(reason => {
                this.checkInProgress = null;

                if (reason.message && reason.message === 'ignore') return false;
                if (reason.isCanceled) return false;

                this.rescheduleCheckForUpdate();
                errorReporting.error(reason);
                this.props.onCheckResult(false);
                return false;
            });
    }

    timeUntilNextCheck = lastCheckTime => {
        if (!lastCheckTime) {
            lastCheckTime = Date.now();
        }
        const now = Date.now();
        const checkIntervalMillis = this.props.checkIntervalInSeconds * 1000;
        if (now < lastCheckTime) {
            return checkIntervalMillis;
        }
        const remainingTime = ((lastCheckTime + checkIntervalMillis) - now);
        return remainingTime > MINIMUM_TIMEOUT ? remainingTime : MINIMUM_TIMEOUT;
    }

    render() {
        return null;
    }
}

UpdateWatcher.propTypes = {
    version: PropTypes.string.isRequired,
    lastCheckTime: PropTypes.number.isRequired,
    active: PropTypes.bool.isRequired,
    checkIntervalInSeconds: PropTypes.number,
    onCheckResult: PropTypes.func.isRequired,
};

UpdateWatcher.defaultProps = {
    checkIntervalInSeconds: Number(process.env.REACT_APP_UPDATE_CHECK_INTERVAL),
}
